import { FaSearch, FaSpinner, FaSync } from "react-icons/fa";
import MessageRow from "./MessageRow";
import PaginationItem from "../ui/components/Pagination";
import NoDataScreen from "../ui/temp-screens/NoDataScreen";
import InboxFilters from "./InboxFilters";
import LoadingScreen from "../ui/temp-screens/LoadingScreen";

const InboxPanel = ({
  accountName,
  folderName,
  messages,
  accounts = [],
  activeAccountId = null,
  selectedUid,
  selectedMessageContext = null,
  loading,
  pagination,
  searchQuery = "",
  onSearchChange,
  isSearchMode = false,
  isUnifiedMode = false,
  filters = {},
  onFiltersApply,
  onFiltersClear,
  connectedAccountIds = [],
  unifiedHasMore = false,
  loadingMoreUnified = false,
  unifiedLoadedCount,
  onLoadMoreUnified,
  onSelectMessage,
  onDeleteMessage,
  onMoveMessage,
  currentFolder = "INBOX",
  onPageChange,
  onRefresh,
  canDelete = true,
}) => {
  const getAccountLabelColorAndTextColor = (msg) => {
    let acc = null;
    if (isUnifiedMode && msg.mailAccountId) {
      acc = accounts.find((a) => a.id === msg.mailAccountId);
      return {
        label: msg.mailAccountName,
        accountEmail: acc?.imapUser || null,
        color: acc?.color || null,
        textColor: acc?.textColor || null,
      };
    }
    if (!isUnifiedMode && activeAccountId) {
      acc = accounts.find((a) => a.id === activeAccountId);
      return {
        label: acc?.nombre || acc?.imapUser || accountName,
        accountEmail: null,
        color: acc?.color || null,
        textColor: acc?.textColor || null,
      };
    }
    return { label: null, accountEmail: null, color: null, textColor: null };
  };
  const isMessageActive = (msg) => {
    if (isUnifiedMode && selectedMessageContext) {
      return msg.uid === selectedMessageContext.uid && msg.mailAccountId === selectedMessageContext.mailAccountId;
    }
    return selectedUid === msg.uid;
  };
  const displayFolder = folderName === "INBOX" ? "Bandeja de entrada" : folderName?.split("/").pop() || "Bandeja";

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3 border-b border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h3 className="text-sm font-bold text-gray-800">{displayFolder}</h3>
            {accountName && (
              <p className="text-[10px] text-gray-400">{accountName}</p>
            )}
          </div>
          <button
            onClick={onRefresh}
            disabled={loading}
            className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500 transition-colors"
            title="Actualizar"
          >
            {loading ? <FaSpinner className="animate-spin text-xs" /> : <FaSync className="text-xs" />}
          </button>
        </div>
        <div className="relative">
          <FaSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-[10px]" />
          <input
            type="text"
            placeholder={isUnifiedMode ? "Buscar en esta lista (asunto, remitente, destinatario)..." : "Buscar en toda la carpeta (asunto, remitente, destinatario, cuerpo)..."}
            value={searchQuery}
            onChange={(e) => onSearchChange?.(e.target.value)}
            className="w-full pl-7 pr-3 py-1.5 text-[11px] border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 bg-gray-50"
          />
        </div>
        {isSearchMode && (
          <p className="text-[10px] text-gray-500 mt-1">
            {pagination.total} resultado{pagination.total !== 1 ? "s" : ""} encontrado{pagination.total !== 1 ? "s" : ""}
          </p>
        )}
        {isUnifiedMode && onFiltersApply && (
          <InboxFilters
            filters={filters}
            onApply={onFiltersApply}
            onClear={onFiltersClear}
            accounts={accounts}
            connectedAccountIds={connectedAccountIds}
          />
        )}
      </div>

      <div className="flex-1 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] relative">
        {loading && messages.length === 0 ? (
          <LoadingScreen text="Cargando mensajes..." />
        ) : messages.length === 0 ? (
          <div className="py-12">
            <NoDataScreen text={isSearchMode ? "Sin resultados para la búsqueda" : "No hay mensajes"} />
          </div>
        ) : (
          <>
            {loading && (
              <LoadingScreen text="Cargando mensajes..." />
            )}
            {messages.map((msg) => {
              const { label, accountEmail, color, textColor } = getAccountLabelColorAndTextColor(msg);
              return (
                <MessageRow
                  key={msg.mailAccountId ? `${msg.mailAccountId}-${msg.uid}` : msg.uid}
                  message={msg}
                  accountColor={color}
                  accountTextColor={textColor}
                  accountLabel={label}
                  accountEmail={accountEmail}
                  isActive={isMessageActive(msg)}
                  onSelect={onSelectMessage}
                  onDelete={onDeleteMessage}
                  onMoveMessage={onMoveMessage}
                  currentFolder={currentFolder}
                  canDelete={canDelete}
                />
              );
            })}
          </>
        )}
      </div>

      {isUnifiedMode && messages.length > 0 && (
        <div className="border-t border-gray-200 px-4 py-3 space-y-2">
          <p className="text-[11px] text-gray-500 text-center">
            <span className="font-semibold text-gray-700">{unifiedLoadedCount ?? messages.length}</span> cargados de{" "}
            <span className="font-semibold text-gray-700">{pagination.total}</span> correo{pagination.total !== 1 ? "s" : ""}
          </p>
          {unifiedHasMore && onLoadMoreUnified && (
            <button
              type="button"
              onClick={onLoadMoreUnified}
              disabled={loadingMoreUnified}
              className="w-full py-2 text-[12px] font-semibold text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loadingMoreUnified ? (
                <>
                  <FaSpinner className="animate-spin text-sm" />
                  Cargando...
                </>
              ) : (
                "Cargar más"
              )}
            </button>
          )}
        </div>
      )}
      {!isUnifiedMode && !isSearchMode && pagination.total > pagination.limit && (
        <div className="border-t border-gray-200 px-2 py-1">
          <PaginationItem
            actualDocs={messages.length}
            totalDocs={pagination.total}
            entity="mensajes"
            totalPages={Math.ceil(pagination.total / pagination.limit)}
            actualPage={pagination.page}
            handlePageChange={(_, page) => onPageChange(page)}
            variant="horizontal"
            siblingCount={1}
            boundaryCount={1}
          />
        </div>
      )}
    </div>
  );
};

export default InboxPanel;
